import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import EventStatus from 'core/enums/eventStatus';
import Event from 'core/models/event';
import { isAfter } from 'date-fns';
import { DELETE, GET, HttpBody, POST, PUT } from 'utils/http';

// Couche d'accès « état serveur » des events via TanStack Query.
// Le cache (staleTime) remplace l'ancien flag Redux `alreadyFetched`, et
// l'invalidation de cette clé remplace les actions Set/Add/Update/Delete + refetch.
export const eventsQueryKey = ['events'] as const;

interface EventsResponse {
    events: Event[];
}

export const fetchEvents = async (): Promise<Event[]> => {
    const response = (await GET(`${BACKEND_URL}/api/events`)) as EventsResponse;

    return response.events ?? [];
};

// Hook de base : équivalent de l'ancien WithEvents provider + state.events.list.
export const useEventsQuery = () =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
    });

// Comparateur identique à l'ancien tri des selectors (croissant par date).
// NB : on copie le tableau ([...events]) avant de trier car `sort` mute en place,
// ce qui corromprait la donnée mise en cache par TanStack Query.
const sortByDateAscending = (events: Event[]): Event[] =>
    [...events].sort((a: Event, b: Event) => {
        if (isAfter(new Date(a.date), new Date(b.date))) {
            return 1;
        }
        return -1;
    });

// eventsSelector -> useEvents()
export const useEvents = () =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
        select: (events: Event[]) => events,
    });

// eventByIdSelector(state, id) -> useEventById(id)
export const useEventById = (id: string | undefined) =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
        select: (events: Event[]) => events.find((event) => event.id === id),
        enabled: Boolean(id),
    });

// eventsSortedByDateSelector -> useEventsSortedByDate()
export const useEventsSortedByDate = () =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
        select: sortByDateAscending,
    });

// notStartedEventsSortedByDateSelector -> useNotStartedEventsSortedByDate()
// On préserve l'ordre exact de l'ancien selector : tri d'abord, puis filtre.
export const useNotStartedEventsSortedByDate = () =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
        select: (events: Event[]) =>
            sortByDateAscending(events).filter((event) => EventStatus.NotStarted === event.status),
    });

// eventsByContactIdSortedByDateSelector(state, id) -> useEventsByContactIdSortedByDate(id)
export const useEventsByContactIdSortedByDate = (id: string | undefined) =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
        select: (events: Event[]) =>
            sortByDateAscending(events.filter((event) => event.contact && event.contact.id === id)),
        enabled: Boolean(id),
    });

// eventsByApplicationIdSortedByDateSelector(state, id) -> useEventsByApplicationIdSortedByDate(id)
export const useEventsByApplicationIdSortedByDate = (id: string | undefined) =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
        select: (events: Event[]) =>
            sortByDateAscending(events.filter((event) => event.application?.id === id)),
        enabled: Boolean(id),
    });

// eventsCount -> useEventsCount()
export const useEventsCount = () =>
    useQuery({
        queryKey: eventsQueryKey,
        queryFn: fetchEvents,
        select: (events: Event[]) => events.length,
    });

// NB : eventsAlreadyFetchedSelector est obsolète, le cache TanStack Query le remplace
// (utiliser !useEventsQuery().isLoading si une garde de chargement est nécessaire).

// À appeler après une mutation pour resynchroniser la liste depuis le serveur.
export const useInvalidateEvents = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: eventsQueryKey });
};

interface CreateOrUpdateEventResponse {
    event: Event;
}

// AddEvent -> useCreateEventMutation()
// Mêmes endpoint et forme de réponse que l'ancien createEventRequest (POST /api/event).
export const useCreateEventMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: HttpBody): Promise<Event> => {
            const response = (await POST<CreateOrUpdateEventResponse>(
                `${BACKEND_URL}/api/event`,
                data,
            )) as CreateOrUpdateEventResponse;

            return response.event;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsQueryKey }),
    });
};

// UpdateEvent -> useUpdateEventMutation()
// Mêmes endpoint et forme de réponse que l'ancien updateEventRequest (PUT /api/event/:id).
export const useUpdateEventMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: HttpBody }): Promise<Event> => {
            const response = (await PUT<CreateOrUpdateEventResponse>(
                `${BACKEND_URL}/api/event/${id}`,
                data,
            )) as CreateOrUpdateEventResponse;

            return response.event;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsQueryKey }),
    });
};

// DeleteEvent -> useDeleteEventMutation()
// Même endpoint que l'ancien deleteEventRequest (DELETE /api/event/:id).
export const useDeleteEventMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => DELETE(`${BACKEND_URL}/api/event/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsQueryKey }),
    });
};
